import {
  BaseQueryFn,
  createApi,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react"
import { EndpointBuilder } from "@reduxjs/toolkit/dist/query/endpointDefinitions"
import {
  RootState,
  dynamicMiddlewaresInstance,
  registerReducer,
} from "@core/store"
import { baseQueryWithReauth } from "@/modules/app/store"
import { ChatAnalyze } from "../chat_analyze/interfaces"
import { baseTGStatApiURL } from "./telegramApi"

// Base URL: same TGSTAT API host, path /telegram/chat-analyze
export const chatAnalyzeBaseUrl = `${
  baseTGStatApiURL.endsWith("/") ? baseTGStatApiURL : baseTGStatApiURL + "/"
}telegram/chat-analyze`

const tagTypes = [
  "SavedChat",
  "Analysis",
  "ChatMember",
  "Intersection",
] as const
const reducerPath = "chatAnalyzeApi"

const baseQuery: BaseQueryFn = fetchBaseQuery({
  baseUrl: chatAnalyzeBaseUrl,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token
    if (token) {
      headers.set("authorization", `Bearer ${token}`)
    }
    return headers
  },
})

type Builder = EndpointBuilder<
  typeof baseQuery,
  (typeof tagTypes)[number],
  typeof reducerPath
>

const endpoints = (build: Builder) => ({
  // GET /chats — глобальный список всех сохранённых чатов.
  // `source` (CSV `manual,guest-bot`) — фильтр по источнику анализов (§1.1);
  // без параметра — видны все чаты, ответ прежний.
  getChats: build.query<ChatAnalyze.SavedChat[], { source?: string } | void>({
    query: (arg) => {
      const source = arg?.source
      return source
        ? { url: "chats", method: "GET", params: { source } }
        : { url: "chats", method: "GET" }
    },
    providesTags: [{ type: "SavedChat", id: "LIST" }],
  }),

  // GET /chats/:chatId/analyses — версии анализов чата (desc by date).
  // `source` — фильтр по источнику строк истории (§1.2), опционален.
  getChatAnalyses: build.query<
    ChatAnalyze.AnalysisSummary[],
    { chatId: string; source?: string }
  >({
    query: ({ chatId, source }) => ({
      url: `chats/${chatId}/analyses`,
      method: "GET",
      ...(source ? { params: { source } } : {}),
    }),
    providesTags: (_result, _err, { chatId }) => [
      { type: "Analysis", id: chatId },
    ],
  }),

  // GET /analyses/:analysisId — полный Stats конкретного анализа
  getAnalysis: build.query<ChatAnalyze.Stats, string>({
    query: (analysisId) => ({ url: `analyses/${analysisId}`, method: "GET" }),
    providesTags: (_result, _err, analysisId) => [
      { type: "Analysis", id: analysisId },
    ],
  }),

  // GET /chats/:chatId/members?page=1&limit=50
  getChatMembers: build.query<
    ChatAnalyze.PagedMembers<ChatAnalyze.ChatMember>,
    { chatId: string; page: number; limit: number }
  >({
    query: ({ chatId, page, limit }) => ({
      url: `chats/${chatId}/members`,
      method: "GET",
      params: { page, limit },
    }),
    providesTags: (_result, _err, { chatId }) => [
      { type: "ChatMember", id: chatId },
    ],
  }),

  // GET /intersection?chatId=<uuid>&source=... → топ-5 пересечений.
  // Дельта 2026-09-22 (BREAKING): кандидатов выбирает сервер из библиотеки,
  // исключая базовый чат; параметр chatIds удалён (400 от ParseUUIDPipe),
  // 404 если базовый чат не найден. Форма ответа прежняя.
  getIntersection: build.query<
    ChatAnalyze.IntersectionRow[],
    { chatId: string; source?: string }
  >({
    query: ({ chatId, source }) => ({
      url: "intersection",
      method: "GET",
      params: source ? { chatId, source } : { chatId },
    }),
    providesTags: ["Intersection"],
  }),

  // GET /intersection/members?chatIds=A,B&page=1&limit=50
  getIntersectionMembers: build.query<
    ChatAnalyze.PagedMembers<ChatAnalyze.IntersectionMember>,
    { chatIds: [string, string]; page: number; limit: number }
  >({
    query: ({ chatIds, page, limit }) => ({
      url: "intersection/members",
      method: "GET",
      params: { chatIds: chatIds.join(","), page, limit },
    }),
  }),

  // POST /chats — сохранить чат; invalidates list cache on success.
  // «Intersection» тоже: раньше пересечения обновлялись косвенно (смена arg
  // после refetch списка чатов), теперь IntersectionSummary не подписан на /chats.
  saveChat: build.mutation<ChatAnalyze.SavedChat, ChatAnalyze.SaveChatRequest>({
    query: (body) => ({ url: "chats", method: "POST", body }),
    invalidatesTags: [
      { type: "SavedChat", id: "LIST" },
      { type: "Intersection" },
    ],
  }),
})

export const chatAnalyzeApi = createApi({
  reducerPath,
  tagTypes,
  baseQuery: baseQueryWithReauth(baseQuery),
  endpoints: (build) => ({ ...endpoints(build) }),
})

dynamicMiddlewaresInstance.addMiddleware(chatAnalyzeApi.middleware)
registerReducer(chatAnalyzeApi.reducerPath, chatAnalyzeApi.reducer)

export const {
  useGetChatsQuery,
  useGetChatAnalysesQuery,
  useGetAnalysisQuery,
  useGetChatMembersQuery,
  useGetIntersectionQuery,
  useGetIntersectionMembersQuery,
  useSaveChatMutation,
} = chatAnalyzeApi
